import type { CategoryDetailsItem, CategoryTreeNode } from '@/api/models';
import type { Category, CategoryParentOption } from './categoriesMeta';

/**
 * Pure tree helpers for the Categories table. Kept separate from the
 * presentational config in `categoriesMeta.ts` and free of React so they can be
 * unit-tested in isolation and reused if the data source changes.
 */

/**
 * Markup over cost as a percentage, one decimal place.
 *
 * <p>Derived from the summed cost and sale figures, never averaged from the
 * children's percentages: a 200% markup on 1 GEL and a 10% markup on 1000 GEL
 * do not average to 105%.</p>
 *
 * <p>Zero cost yields zero rather than infinity — matching what the API
 * returns for a leaf in the same state.</p>
 */
export const markupPercent = (
    totalCostTetri: number,
    totalSalePriceTetri: number,
) =>
    totalCostTetri === 0
        ? 0
        : Math.round(
              ((totalSalePriceTetri - totalCostTetri) / totalCostTetri) * 1000,
          ) / 10;

/**
 * Map the API's nested `CategoryTreeNode`s into table rows, merging in the
 * per-category totals from `/categories/details` and rolling them up the tree.
 *
 * <p>`/tree` carries the hierarchy and no figures; `/details` carries the
 * figures and no hierarchy, but both key on the same ids. Reading only the
 * tree is what showed 0 products and 0.00 everywhere while `/details`
 * answered `productCount: 1, totalCost: 100.80` for the same row.</p>
 *
 * <p>A parent's figures are its own plus every descendant's. A category with
 * products of its own <em>and</em> subcategories is a real shape, so the
 * node's own totals are included rather than replaced by the children's.</p>
 */
export const mapTreeNodesToRows = (
    nodes: CategoryTreeNode[],
    resolveImageUrl: (url: string | null) => string | null,
    statsById: ReadonlyMap<string, CategoryDetailsItem> = new Map(),
): Category[] =>
    nodes.map((node) => {
        const children = mapTreeNodesToRows(
            node.children,
            resolveImageUrl,
            statsById,
        );
        const own = statsById.get(node.id);
        const productCount =
            (own?.productCount ?? 0) +
            children.reduce((sum, child) => sum + child.productCount, 0);
        // BOS-162: the rollup adds integers. It used to add the decimals the
        // wire sent, so a deep tree's parent could disagree with the sum of the
        // rows printed under it by a tetri or two.
        const totalCostTetri =
            (own?.totalCostTetri ?? 0) +
            children.reduce((sum, child) => sum + child.totalCostTetri, 0);
        const totalSalePriceTetri =
            (own?.totalSalePriceTetri ?? 0) +
            children.reduce((sum, child) => sum + child.totalSalePriceTetri, 0);

        return {
            id: node.id,
            name: node.name,
            slug: node.slug,
            imageUrl: resolveImageUrl(node.imageUrl),
            parentId: node.parentId,
            productCount,
            totalCostTetri,
            totalSalePriceTetri,
            totalMarkup: markupPercent(totalCostTetri, totalSalePriceTetri),
            children,
        };
    });

/** Index a `/categories/details` page by id, for the tree merge above. */
export const indexStatsById = (
    items: readonly CategoryDetailsItem[],
): ReadonlyMap<string, CategoryDetailsItem> =>
    new Map(items.map((item) => [item.id, item]));

/**
 * Filter the tree by a search query against name/slug, preserving structure.
 * A node is kept when it matches directly or has a kept descendant; a kept
 * node's children are the filtered children, so non-matching branches are
 * pruned while the path to every match is retained.
 */
export const filterTree = (rows: Category[], query: string): Category[] => {
    const q = query.trim().toLowerCase();
    if (!q) {
        return rows;
    }

    const walk = (nodes: Category[]): Category[] =>
        nodes.reduce<Category[]>((kept, node) => {
            const filteredChildren = walk(node.children);
            const selfMatch =
                node.name.toLowerCase().includes(q) ||
                node.slug.toLowerCase().includes(q);
            if (selfMatch || filteredChildren.length > 0) {
                kept.push({ ...node, children: filteredChildren });
            }
            return kept;
        }, []);

    return walk(rows);
};

/** Depth-first lookup of a single row by id. */
export const findCategory = (rows: Category[], id: string): Category | null => {
    for (const row of rows) {
        if (row.id === id) {
            return row;
        }
        const found = findCategory(row.children, id);
        if (found) {
            return found;
        }
    }
    return null;
};

/** Collect a row's id plus every descendant id (its whole subtree). */
export const collectSubtreeIds = (row: Category): string[] => {
    const ids = [row.id];
    for (const child of row.children) {
        ids.push(...collectSubtreeIds(child));
    }
    return ids;
};

/**
 * Flatten the tree into an indented list for the parent picker. Rows whose id
 * is in `excludedIds` (the category being edited and its descendants — which
 * can't become their own ancestor) are skipped along with their subtree.
 */
export const flattenForParentPicker = (
    rows: Category[],
    excludedIds: ReadonlySet<string> = new Set(),
): CategoryParentOption[] => {
    const out: CategoryParentOption[] = [];
    const walk = (nodes: Category[], depth: number): void => {
        for (const node of nodes) {
            if (excludedIds.has(node.id)) {
                continue;
            }
            out.push({ id: node.id, name: node.name, depth });
            walk(node.children, depth + 1);
        }
    };
    walk(rows, 0);
    return out;
};
